import React, { useState, useRef } from "react";
import {
  IconButton,
  Box,
  TextField as MuiTextField,
  Popper,
  Paper,
  Button,
  ClickAwayListener,
  Typography,
  InputAdornment,
  FormControl,
  MenuItem
} from "@mui/material";
import {
  Search,
  //ArrowDropDown,
  //ArrowDropUp,
  DateRange
} from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import { Formik, Form, Field } from "formik";
import { TextField, Select } from "formik-mui";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import ButtonGroup from "../../../../components/Buttons/ButtonGroup";
import { enterKeyCode } from "../../../../constants";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

const useStyles = makeStyles(theme => ({
  root: {
    transition: "width 0.3s",
    width: 40
  },
  open: {
    width: 256,
    backgroundColor: "#E3F2FD"
  },
  formControl: {
    "& > *:only-child > input": {
      padding: 0
    }
  },
  menu: {
    padding: theme.spacing(3)
  },
  field: {
    marginBottom: theme.spacing(2),
    "& > *:only-child > div, > *:only-child > input": {
      paddingTop: theme.spacing(1.25),
      paddingBottom: theme.spacing(1.25)
    }
  }
}));

const SelectField = ({ fieldName, label, onExited, onEnter, options }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <FormControl fullWidth className={classes.field}>
      <Field
        MenuProps={{
          onEnter,
          onExited
        }}
        type="text"
        name={fieldName}
        component={Select}
        inputProps={{
          name: fieldName,
          id: fieldName
        }}
        variant="filled"
        fullWidth
        displayEmpty
        disableUnderline
        renderValue={value => {
          return (
            <Typography
              variant="subtitle1"
              color={value === "" ? "textSecondary" : "textPrimary"}
            >
              {value === "" ? label : value}
            </Typography>
          );
        }}
      >
        <MenuItem value="">{t("manageRepository.searchOptionAll")}</MenuItem>
        {options &&
          options.map((option, index) => (
            <MenuItem key={index} value={option}>
              {option}
            </MenuItem>
          ))}
      </Field>
    </FormControl>
  );
};

const DatePickerField = ({ fieldName, open, onOpen, onClose, placeholder }) => {
  const classes = useStyles();
  return (
    <Field
      name={fieldName}
      classes={{ formControl: classes.field }}
      component={({
        name,
        form: { setFieldValue, isSubmitting },
        field: { value }
      }) => {
        return (
          <DatePicker
            open={open}
            onOpen={onOpen}
            onClose={onClose}
            className={classes.field}
            disabled={isSubmitting}
            name={name}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <DateRange color="action" />
                </InputAdornment>
              ),
              disableUnderline: true
            }}
            inputVariant="filled"
            onChange={value => {
              setFieldValue(fieldName, value);
            }}
            format="yyyy-MM-dd"
            value={value}
            animateYearScrolling={false}
            placeholder={placeholder}
          />
        );
      }}
    />
  );
};

// TODO: Implement search functionality & retrieving options for file type & creator dropdowns
const ManageRepositorySearch = ({ disabled, handleSearchItem }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(false);
  const [uploadedBeforePickerOpen, setUploadedBeforePickerOpen] = useState(
    false
  );
  const [uploadedAfterPickerOpen, setUploadedAfterPickerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuRef = useState(null);
  const [fieldMenuOpen, setFieldMenuOpen] = useState(false);
  const textFieldRef = useRef(null);
  const inputRef = useRef(null);
  /**
   * If the user clicks outside of the menu but not within the search field,
   * close the advanced search menu
   */
  const handleOutsideClick = e => {
    if (
      !(
        e.target === inputRef.current ||
        e.target === textFieldRef.current.children[0]
      ) &&
      !fieldMenuOpen &&
      !uploadedAfterPickerOpen &&
      !uploadedBeforePickerOpen
    ) {
      setMenuExpanded(false);
    }
  };

  React.useEffect(() => {
    if (open) {
      inputRef.current.focus();
    }
  }, [open]);

  return (
    <Box
      ref={node => setAnchorEl(node)}
      className={clsx(classes.root, { [classes.open]: open })}
      display="flex"
      alignItems="center"
    >
      <IconButton
        disabled={disabled}
        onClick={() => {
          setOpen(true);
        }}
        color="primary"
        size="large"
      >
        <Search />
      </IconButton>
      {open && (
        <Formik
          initialValues={{
            fileType: "",
            creator: "",
            uploadedBefore: null,
            uploadedAfter: null,
            fileName: "",
            tags: "",
            comments: ""
          }}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
          }}
        >
          {({ submitForm, resetForm }) => (
            <Form>
              <MuiTextField
                ref={textFieldRef}
                inputRef={inputRef}
                onKeyDown={event => {
                  if (event.keyCode === enterKeyCode) {
                    handleSearchItem(event);
                  }
                }}
                InputProps={{
                  disableUnderline: true
                  //remove comment when advanced search will be implemented
                  /*endAdornment: (
                    <IconButton onClick={() => setMenuExpanded(!menuExpanded)}>
                      {menuExpanded ? (
                        <ArrowDropUp color="primary" />
                      ) : (
                        <ArrowDropDown color="primary" />
                      )}
                    </IconButton>
                  )*/
                }}
                classes={{ root: classes.formControl }}
              />
              <Popper anchorEl={anchorEl} open={menuExpanded}>
                <ClickAwayListener onClickAway={handleOutsideClick}>
                  <Paper
                    ref={menuRef}
                    className={classes.menu}
                    square
                    style={{
                      maxWidth: anchorEl ? anchorEl.clientWidth : undefined
                    }}
                  >
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <Box pb={1.5}>
                        <Typography variant="subtitle2">
                          {t("manageRepository.searchHeader")}
                        </Typography>
                      </Box>
                      <SelectField
                        fieldName="fileType"
                        label={t("manageRepository.searchLabelFileType")}
                        onExited={() => {
                          setFieldMenuOpen(false);
                        }}
                        onEnter={() => setFieldMenuOpen(true)}
                        options={["PDF", "SCORM", "Image"]}
                      />
                      <SelectField
                        fieldName="creator"
                        label={t("manageRepository.searchLabelCreator")}
                        options={["Jane Hill", "John Hill"]}
                        onExited={() => {
                          setFieldMenuOpen(false);
                        }}
                        onEnter={() => setFieldMenuOpen(true)}
                      />
                      <DatePickerField
                        open={uploadedBeforePickerOpen}
                        onOpen={() => setUploadedBeforePickerOpen(true)}
                        onClose={() => setUploadedBeforePickerOpen(false)}
                        placeholder={t(
                          "manageRepository.searchLabelUploadedBefore"
                        )}
                        fieldName="uploadedBefore"
                      />
                      <DatePickerField
                        open={uploadedAfterPickerOpen}
                        onOpen={() => setUploadedAfterPickerOpen(true)}
                        onClose={() => setUploadedAfterPickerOpen(false)}
                        placeholder={t(
                          "manageRepository.searchLabelUploadedAfter"
                        )}
                        fieldName="uploadedAfter"
                      />
                      <Field
                        placeholder={t("manageRepository.searchLabelFileName")}
                        classes={{ root: classes.field }}
                        name={"fileName"}
                        variant="filled"
                        fullWidth
                        component={TextField}
                      />
                      <Field
                        placeholder={t("manageRepository.searchLabelTags")}
                        classes={{ root: classes.field }}
                        name={"tags"}
                        variant="filled"
                        fullWidth
                        component={TextField}
                      />
                      <Field
                        placeholder={t("manageRepository.searchLabelComments")}
                        classes={{ root: classes.field }}
                        name={"comments"}
                        variant="filled"
                        fullWidth
                        component={TextField}
                      />
                    </LocalizationProvider>
                    <Box display="flex" justifyContent="flex-end" mt={1.5}>
                      <ButtonGroup>
                        <Button
                          color="primary"
                          onClick={() => {
                            resetForm();
                            setMenuExpanded(false);
                          }}
                        >
                          {t("manageRepository.searchBtnCancel")}
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            // TODO: Implement search functionality
                            submitForm();
                          }}
                        >
                          {t("manageRepository.searchBtnSubmit")}
                        </Button>
                      </ButtonGroup>
                    </Box>
                  </Paper>
                </ClickAwayListener>
              </Popper>
            </Form>
          )}
        </Formik>
      )}
    </Box>
  );
};

export default ManageRepositorySearch;
